import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Textarea from '~/components/Textarea'

// Schemas de validación
const loginSchema = z.object({
  email: z.string().email('Ingresa un correo electrónico válido'),
  password: z.string().min(8, 'La seguridad es primero: mínimo 8 caracteres'),
  remember: z.boolean().optional()
})

const profileSchema = z.object({
  fullName: z.string().min(3, 'Nombre demasiado corto'),
  bio: z.string().max(200, 'Máximo 200 caracteres para la bio'),
  category: z.string().min(1, 'Selecciona una categoría'),
  notifications: z.boolean().optional()
})

const FormPage: Component = () => {
  const [loginResult, setLoginResult] = createSignal<unknown>(null)

  const formDataToObj = (fd: FormData) => {
    const obj: Record<string, unknown> = {}
    fd.forEach((v, k) => {
      const current = obj[k]
      if (current) {
        if (!Array.isArray(current)) obj[k] = [current]
        ;(obj[k] as unknown[]).push(v)
      } else {
        if (v === 'true') obj[k] = true
        else if (v === 'false') obj[k] = false
        else obj[k] = v
      }
    })
    return obj
  }

  return (
    <>
      <Title>Form - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Form</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Arquitectura de captura de datos con validación Zod. El componente Form centraliza la lógica de envío, gestión de estados de error y validación en tiempo real para experiencias de usuario
            fluidas.
          </p>
        </div>

        <div class="space-y-12">
          {/* Validación con Zod (Estándar) */}
          <Card>
            <Card.Header
              title="Integración con Zod"
              subtitle="Validación automática basada en schemas. Los errores se inyectan directamente en los inputs."
              icon={<Icon icon="material-symbols-light:security" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div class="p-8 bg-surface-muted rounded-2xl border border-border">
                  <Form
                    validator={loginSchema}
                    onSubmit={fd => {
                      setLoginResult(formDataToObj(fd))
                    }}
                    class="space-y-6"
                  >
                    <Input name="email" type="email" label="Correo Electrónico" placeholder="ejemplo@dominio.com" startIcon="material-symbols-light:mail-outline" />
                    <Input name="password" type="password" label="Contraseña" placeholder="Crea una clave segura" startIcon="material-symbols-light:lock-outline" />
                    <div class="flex justify-between items-center">
                      <Checkbox name="remember" label="Mantener sesión" />
                      <Button variant="text" size="sm" color="primary">
                        ¿Olvidaste tu clave?
                      </Button>
                    </div>
                    <Button type="submit" variant="contained" color="primary" fullWidth size="lg">
                      Entrar al Sistema
                    </Button>
                  </Form>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-indigo-500">Resultado del Parser</h4>
                  <Show
                    when={loginResult()}
                    fallback={
                      <div class="p-10 border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center opacity-40">
                        <Icon icon="material-symbols-light:code" size={48} />
                        <p class="text-sm mt-4">Envía el formulario para ver los datos</p>
                      </div>
                    }
                  >
                    <div class="animate-in zoom-in-95 duration-300">
                      <Alert variant="success" icon="material-symbols-light:check-circle-outline" title="Envío Exitoso">
                        Los datos han sido validados por Zod y están listos para la API.
                      </Alert>
                      <pre class="mt-4 p-6 bg-slate-900 text-emerald-400 rounded-xl font-mono text-xs overflow-auto shadow-xl ring-1 ring-white/10">{JSON.stringify(loginResult(), null, 2)}</pre>
                    </div>
                  </Show>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Formulario de Perfil (Multi-componente) */}
          <Card>
            <Card.Header
              title="Composición de Controles"
              subtitle="Combina múltiples tipos de input bajo un mismo contexto de formulario."
              icon={<Icon icon="material-symbols-light:account-circle-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-4xl mx-auto">
                <Form validator={profileSchema} onSubmit={_fd => alert('Perfil guardado')} class="bg-surface p-10 rounded-3xl border border-border shadow-2xl relative">
                  <div class="absolute -top-6 left-1/2 -translate-x-1/2 px-6 py-2 bg-primary-600 text-white rounded-full text-xs font-black tracking-widest uppercase shadow-lg">Editar Perfil</div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <Input name="fullName" label="Nombre Completo" startIcon="material-symbols-light:badge-outline" />
                    <Select
                      name="category"
                      label="Especialidad"
                      options={[
                        { title: 'Backend Developer', value: 'be' },
                        { title: 'Frontend Developer', value: 'fe' },
                        { title: 'UI/UX Designer', value: 'ui' }
                      ]}
                    />
                  </div>

                  <Textarea name="bio" label="Biografía Profesional" helperText="Cuéntanos un poco sobre tu trayectoria." />

                  <div class="mt-10 p-6 bg-surface-muted rounded-2xl flex items-center justify-between">
                    <div class="space-y-1">
                      <h5 class="text-sm font-bold">Notificaciones Push</h5>
                      <p class="text-xs text-foreground-subtle">Recibe alertas sobre mensajes nuevos.</p>
                    </div>
                    <Switch name="notifications" />
                  </div>

                  <div class="mt-10 flex gap-4">
                    <Button type="submit" variant="contained" color="secondary" pill class="px-10">
                      Guardar Cambios
                    </Button>
                    <Button type="button" variant="soft">
                      Restablecer
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>

          {/* Render Props (Control de Estado Interno) */}
          <Card>
            <Card.Header
              title="Estado Reactivo (Render Props)"
              subtitle="Accede a la validez y los valores del formulario antes del envío para lógica condicional en la UI."
              icon={<Icon icon="material-symbols-light:code" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 bg-slate-900 rounded-2xl border border-slate-800 text-white shadow-2xl overflow-hidden relative">
                <div class="absolute right-0 bottom-0 p-10 opacity-5 -mr-10 -mb-10">
                  <Icon icon="material-symbols-light:checklist" size={240} />
                </div>

                <Form validator={loginSchema} class="relative z-10 max-w-lg mx-auto">
                  {state => (
                    <div class="space-y-8">
                      <div class="flex items-center justify-between border-b border-white/10 pb-6">
                        <div class="flex gap-2 items-center">
                          <Badge color={state.valid() ? 'success' : 'danger'} size="sm" dot />
                          <span class="text-xs font-bold uppercase tracking-widest">{state.valid() ? 'Ready to Send' : 'Fix Errors'}</span>
                        </div>
                        <div class="text-[10px] font-mono text-foreground-subtle">STATE_OBSERVER_ACTIVE</div>
                      </div>

                      <Input name="email" label="Validación Reactiva" />

                      <Show when={!state.valid()}>
                        <Alert variant="warning" class="bg-amber-500/10 border-amber-500/20 text-amber-200 text-xs">
                          Completa los campos para habilitar el motor de envío.
                        </Alert>
                      </Show>

                      <Button
                        type="submit"
                        disabled={!state.valid()}
                        fullWidth
                        color="success"
                        variant="contained"
                        size="lg"
                        icon={state.valid() ? 'material-symbols-light:bolt-outline' : 'material-symbols-light:lock-outline'}
                      >
                        {state.valid() ? 'Sincronizar Datos' : 'Envío Bloqueado'}
                      </Button>
                    </div>
                  )}
                </Form>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del motor de formularios." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left text-foreground-subtle">
                  <thead class="text-xs uppercase bg-surface-muted">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">validator</td>
                      <td class="px-6 py-4 italic text-xs">ZodSchema</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Schema de Zod para validación de datos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onSubmit</td>
                      <td class="px-6 py-4 italic text-xs">(fd: FormData) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Callback disparado tras validación exitosa.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">initialValues</td>
                      <td class="px-6 py-4 italic text-xs">Record{'<string, any>'}</td>
                      <td class="px-6 py-4 font-mono">{}</td>
                      <td class="px-6 py-4 text-balance">Datos para rellenar el formulario al cargar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">validateOn</td>
                      <td class="px-6 py-4 italic text-xs">'change' | 'blur' | 'submit'</td>
                      <td class="px-6 py-4 font-mono">'submit'</td>
                      <td class="px-6 py-4 italic">Momento en que se dispara la validación del schema.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold italic">children</td>
                      <td class="px-6 py-4 italic text-[10px]">JSX.Element | ((state: FormState) =&gt; JSX)</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Contenido o función render prop.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default FormPage
